// System-administration console (PD-003), served on platform hosts only. Separate session and
// navigation from organisation users; no organisation records beyond what administration needs.
import {
  MFA_REQUIREMENTS,
  POLICY_BOUNDS,
  type MfaRequirement,
  type PlatformSessionResponse,
  type SecurityPolicyResponse,
  type TenantCreated,
  type TenantDetail,
  type TenantSummary,
} from '@passage/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, NavLink, useNavigate, useParams } from 'react-router';
import { ApiRequestError, api, get, setCsrfToken } from '../api.ts';
import { Brand, Dialog, ErrorState, ErrorSummary, Field, Icon, Loading, PageHead, StatusBadge } from '../components/ui.tsx';
import { LanguageSwitch, UnreviewedNotice, useI18n } from '../i18n.tsx';
import { PLATFORM_SESSION_KEY } from '../pages/SignIn.tsx';
import { AccountSecurity } from '../pages/Account.tsx';
import { ActivationLinkDialog, POLICY_LABELS } from '../pages/Users.tsx';
import { PlatformIdentityPanel } from './PlatformIdentity.tsx';
import { PlatformTenantEmailPanel } from './PlatformEmail.tsx'; // PB-13-04 (email stream)

const P = '/api/platform/v1';

export function usePlatformSession(enabled = true) {
  return useQuery({
    queryKey: PLATFORM_SESSION_KEY,
    queryFn: async () => {
      try {
        const s = await get<PlatformSessionResponse>(`${P}/session`);
        setCsrfToken(s.csrfToken);
        return s;
      } catch (e) {
        if (e instanceof ApiRequestError && e.status === 401) return null;
        throw e;
      }
    },
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

function orgUrl(hostname: string, path: string): string {
  const port = window.location.port ? `:${window.location.port}` : '';
  return `${window.location.protocol}//${hostname}${port}${path}`;
}

export function PlatformShell({ children }: { children: ReactNode }) {
  const { t, adopt, setPersist } = useI18n();
  const session = usePlatformSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const accountLocale = session.data?.account.locale;
  useEffect(() => {
    if (accountLocale) adopt(accountLocale);
  }, [adopt, accountLocale]);
  useEffect(() => {
    setPersist((l) => void api<PlatformSessionResponse>('PATCH', `${P}/me/preferences`, { locale: l }).then((s) => qc.setQueryData(PLATFORM_SESSION_KEY, s)));
    return () => setPersist(null);
  }, [qc, setPersist]);
  const signOut = async () => {
    try {
      await api('POST', `${P}/auth/logout`);
    } finally {
      setCsrfToken(null);
      qc.clear();
      qc.setQueryData(PLATFORM_SESSION_KEY, null);
      navigate('/sign-in');
    }
  };
  return (
    <>
      <a className="skip" href="#workspace">
        {t('shell.skip')}
      </a>
      <aside className="sidebar" aria-label={t('shell.openNav')}>
        <NavLink to="/" className="brand">
          <Brand />
        </NavLink>
        <nav className="navscroll">
          <div className="navgroup">{t('platform.title')}</div>
          <NavLink to="/" end className={({ isActive }) => `navlink${isActive ? ' active' : ''}`}>
            <Icon name="sites" />
            {t('platform.nav.organisations')}
          </NavLink>
          <NavLink to="/policy" className={({ isActive }) => `navlink${isActive ? ' active' : ''}`}>
            <Icon name="privacy" />
            {t('platform.nav.policy')}
          </NavLink>
          {/* PB-13-04 (PD-580): installation email delivery settings. */}
          <NavLink to="/email" className={({ isActive }) => `navlink${isActive ? ' active' : ''}`}>
            <Icon name="communications" />
            {t('eml.nav')}
          </NavLink>
          {/* PD-710 (settings stream): runtime settings that used to be environment variables. */}
          <NavLink to="/settings" className={({ isActive }) => `navlink${isActive ? ' active' : ''}`}>
            <Icon name="operations" />
            {t('set.nav')}
          </NavLink>
        </nav>
        <div className="sidebar-footer">
          <span>{t('platform.eyebrow')}</span>
        </div>
      </aside>
      <div className="shell">
        <header className="topbar">
          <NavLink className="role-identity" to="/account" title={t('mfa.account.title')}>
            <div className="avatar" aria-hidden="true">
              SA
            </div>
            <div>
              <strong>{session.data?.account.displayName}</strong>
              <div className="role-subtitle" data-ltr>
                {session.data?.account.email}
              </div>
            </div>
          </NavLink>
          <div className="grow" />
          <LanguageSwitch compact />
          <button className="button small" type="button" onClick={() => void signOut()}>
            <Icon name="logout" />
            {t('shell.signOut')}
          </button>
        </header>
        <div className="env-strip">
          {import.meta.env.DEV ? (
            <>
              <b>{t('shell.envDev')}</b>
              <span>{t('shell.envDevNote')}</span>
            </>
          ) : null}
          <UnreviewedNotice />
        </div>
        <main id="workspace" tabIndex={-1}>
          {children}
        </main>
      </div>
    </>
  );
}

export function OrganisationsPage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform', 'tenants'], queryFn: () => get<{ items: TenantSummary[] }>(`${P}/tenants`) });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<TenantCreated | null>(null);
  return (
    <>
      <PageHead
        eyebrow={t('platform.eyebrow')}
        title={t('platform.orgs.title')}
        intro={t('platform.orgs.intro')}
        actions={
          <button className="button primary" type="button" onClick={() => setCreating(true)}>
            {t('platform.orgs.create')}
          </button>
        }
      />
      <section className="panel">
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <div className="pad">
            <ErrorState error={q.error} retry={() => void q.refetch()} />
          </div>
        ) : (
          <div className="tablewrap" tabIndex={0} data-scroll-hint={t('common.scrollHint')}>
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('platform.orgs.col.name')}</th>
                  <th scope="col">{t('platform.orgs.col.addresses')}</th>
                  <th scope="col">{t('platform.orgs.col.sites')}</th>
                  <th scope="col">{t('platform.orgs.col.entitlements')}</th>
                  <th scope="col">{t('platform.orgs.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <Link to={`/organisations/${o.id}`}>
                        <strong>{o.name}</strong>
                      </Link>
                      <small data-ltr>{o.slug}</small>
                    </td>
                    <td>
                      {o.hostnames.map((h) => (
                        <div key={h} data-ltr>
                          {h}
                        </div>
                      ))}
                    </td>
                    <td>
                      {o.activeSites}
                      {o.maxActiveSites ? ` / ${o.maxActiveSites}` : ''}
                    </td>
                    <td>
                      {o.entitlements.map((e) => (
                        <span key={e} className="badge blue" data-ltr>
                          {e}
                        </span>
                      ))}
                    </td>
                    <td>
                      <StatusBadge status={o.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {creating ? (
        <CreateOrganisationDialog
          onClose={() => setCreating(false)}
          onCreated={(r) => {
            setCreating(false);
            setCreated(r);
            void qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
          }}
        />
      ) : null}
      {created ? (
        <ActivationLinkDialog
          name={created.firstAdmin.email}
          activation={{ activationToken: created.activationToken, expiresAt: created.activationExpiresAt }}
          url={orgUrl(created.signInUrlHost, `/activate#token=${created.activationToken}`)}
          onClose={() => setCreated(null)}
        />
      ) : null}
    </>
  );
}

function CreateOrganisationDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (r: TenantCreated) => void }) {
  const { t } = useI18n();
  const [f, setF] = useState({ slug: '', name: '', hostname: '', multiSite: false, maxActiveSites: '', adminEmail: '', adminName: '' });
  const m = useMutation({
    mutationFn: () =>
      api<TenantCreated>('POST', `${P}/tenants`, {
        slug: f.slug,
        name: f.name,
        hostname: f.hostname,
        multiSite: f.multiSite,
        maxActiveSites: f.maxActiveSites ? Number(f.maxActiveSites) : null,
        firstAdmin: { email: f.adminEmail, displayName: f.adminName },
      }),
    onSuccess: onCreated,
  });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Dialog open title={t('platform.orgs.create')} onClose={onClose}>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorSummary error={m.error} fieldIds={{ slug: 'o-slug', name: 'o-name', hostname: 'o-host', 'firstAdmin.email': 'o-ae', 'firstAdmin.displayName': 'o-an' }} />
        <div className="formgrid">
          <Field id="o-name" label={t('platform.org.name')} error={fe.name}>
            {(p) => <input {...p} value={f.name} onChange={set('name')} />}
          </Field>
          <Field id="o-slug" label={t('platform.org.slug')} error={fe.slug}>
            {(p) => <input {...p} value={f.slug} onChange={set('slug')} dir="ltr" />}
          </Field>
          <Field id="o-host" label={t('platform.org.hostname')} error={fe.hostname} full>
            {(p) => <input {...p} value={f.hostname} onChange={set('hostname')} dir="ltr" placeholder="example.localhost" />}
          </Field>
          <label className="field">
            <span>
              <input type="checkbox" checked={f.multiSite} onChange={(e) => setF((x) => ({ ...x, multiSite: e.target.checked }))} /> {t('platform.org.multiSite')}
            </span>
          </label>
          <Field label={t('platform.org.maxSites')} hint={t('platform.org.maxSitesHint')} error={fe.maxActiveSites}>
            {(p) => <input {...p} type="number" min={1} value={f.maxActiveSites} onChange={set('maxActiveSites')} />}
          </Field>
          <Field id="o-ae" label={t('platform.org.firstAdminEmail')} error={fe['firstAdmin.email']}>
            {(p) => <input {...p} type="email" dir="ltr" value={f.adminEmail} onChange={set('adminEmail')} />}
          </Field>
          <Field id="o-an" label={t('platform.org.firstAdminName')} error={fe['firstAdmin.displayName']}>
            {(p) => <input {...p} value={f.adminName} onChange={set('adminName')} />}
          </Field>
        </div>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="button primary" type="submit" disabled={m.isPending}>
            {m.isPending ? t('common.saving') : t('platform.orgs.create')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function OrganisationPage() {
  const { tenantId = '' } = useParams();
  const { t } = useI18n();
  const qc = useQueryClient();
  const key = ['platform', 'tenant', tenantId];
  const q = useQuery({ queryKey: key, queryFn: () => get<TenantDetail>(`${P}/tenants/${tenantId}`) });
  const policy = useQuery({ queryKey: [...key, 'policy'], queryFn: () => get<SecurityPolicyResponse>(`${P}/tenants/${tenantId}/policy`) });
  const [issued, setIssued] = useState<{ name: string; token: string; expiresAt: string } | null>(null);
  const [newHost, setNewHost] = useState('');
  const [reason, setReason] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
  };
  const patch = useMutation({ mutationFn: (body: Record<string, unknown>) => api<TenantDetail>('PATCH', `${P}/tenants/${tenantId}`, { expectedVersion: q.data?.version, reason: reason || 'Console change', ...body }), onSuccess: refresh });
  const addHost = useMutation({ mutationFn: () => api('POST', `${P}/tenants/${tenantId}/domains`, { hostname: newHost }), onSuccess: () => { setNewHost(''); refresh(); } });
  const removeHost = useMutation({ mutationFn: (h: string) => api('DELETE', `${P}/tenants/${tenantId}/domains/${encodeURIComponent(h)}`), onSuccess: refresh });
  const addAdmin = useMutation({
    mutationFn: () => api<{ userId: string; activationToken: string; activationExpiresAt: string }>('POST', `${P}/tenants/${tenantId}/administrators`, { email: adminEmail, displayName: adminName }),
    onSuccess: (r) => {
      setIssued({ name: adminEmail, token: r.activationToken, expiresAt: r.activationExpiresAt });
      setAdminEmail('');
      setAdminName('');
      refresh();
    },
  });
  const link = useMutation({
    mutationFn: (u: { id: string; email: string }) => api<{ activationToken: string; expiresAt: string }>('POST', `${P}/tenants/${tenantId}/users/${u.id}/activation`).then((r) => ({ ...r, email: u.email })),
    onSuccess: (r) => setIssued({ name: r.email, token: r.activationToken, expiresAt: r.expiresAt }),
  });
  const mfaReset = useMutation({ mutationFn: (userId: string) => api('POST', `${P}/tenants/${tenantId}/users/${userId}/mfa/reset`), onSuccess: refresh });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} />;
  const o = q.data;
  const multi = o.entitlements.includes('multi_site');
  return (
    <>
      <PageHead eyebrow={t('platform.eyebrow')} title={o.name} intro={<span data-ltr>{o.slug}</span>} actions={<StatusBadge status={o.status} />} />
      <ErrorSummary error={patch.error ?? addHost.error ?? removeHost.error ?? addAdmin.error ?? link.error ?? mfaReset.error} fieldIds={{}} />
      <Field label={t('common.reason')} hint={t('common.reasonHint')}>
        {(p) => <input {...p} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} style={{ inlineSize: '100%' }} />}
      </Field>
      <div className="cols" style={{ marginBlockStart: 20 }}>
        <section className="panel">
          <div className="panel-head">
            <h2>{t('platform.orgs.col.addresses')}</h2>
          </div>
          <div className="pad">
            {o.hostnames.map((h) => (
              <div key={h} className="row" style={{ marginBlockEnd: 8 }}>
                <a href={orgUrl(h, '/sign-in')} data-ltr>
                  {h}
                </a>
                <button className="button small" type="button" onClick={() => removeHost.mutate(h)} disabled={o.hostnames.length <= 1}>
                  ✕
                </button>
              </div>
            ))}
            <form
              className="row"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                addHost.mutate();
              }}
            >
              <input aria-label={t('platform.org.hostname')} value={newHost} onChange={(e) => setNewHost(e.target.value)} dir="ltr" placeholder="example.localhost" />
              <button className="button small" type="submit">
                +
              </button>
            </form>
          </div>
        </section>
        <section className="panel">
          <div className="panel-head">
            <h2>{t('platform.org.entitlements')}</h2>
          </div>
          <div className="pad stack">
            <label>
              <input type="checkbox" checked={multi} onChange={(e) => patch.mutate({ entitlements: e.target.checked ? [...o.entitlements, 'multi_site'] : o.entitlements.filter((x) => x !== 'multi_site') })} />{' '}
              {t('platform.org.multiSite')}
            </label>
            <Field label={t('platform.org.maxSites')} hint={t('platform.org.maxSitesHint')}>
              {(p) => (
                <input
                  {...p}
                  type="number"
                  min={1}
                  defaultValue={o.maxActiveSites ?? ''}
                  onBlur={(e) => {
                    const v = e.target.value ? Number(e.target.value) : null;
                    if (v !== o.maxActiveSites) patch.mutate({ maxActiveSites: v });
                  }}
                />
              )}
            </Field>
            <p className="tiny">
              {t('platform.orgs.col.sites')}: {o.activeSites}
            </p>
            {o.availableEntitlements
              .filter((e) => e.code !== 'multi_site')
              .map((e) => (
                <label key={e.code} title={e.description}>
                  <input
                    type="checkbox"
                    checked={o.entitlements.includes(e.code)}
                    onChange={(ev) => patch.mutate({ entitlements: ev.target.checked ? [...o.entitlements, e.code] : o.entitlements.filter((x) => x !== e.code) })}
                  />{' '}
                  <span data-ltr>{e.code}</span>
                </label>
              ))}
            <button className="button danger" type="button" onClick={() => patch.mutate({ status: o.status === 'active' ? 'suspended' : 'active' })}>
              {o.status === 'active' ? t('platform.org.suspend') : t('platform.org.reactivate')}
            </button>
          </div>
        </section>
      </div>
      <section className="panel">
        <div className="panel-head">
          <h2>{t('platform.org.users')}</h2>
        </div>
        <div className="pad">
          {o.administrators.map((a) => (
            <div key={a.id} className="row" style={{ marginBlockEnd: 8 }}>
              <span>
                <strong>{a.displayName}</strong> <small data-ltr>{a.email}</small> <StatusBadge status={a.status} />
              </span>
              <span className="actions">
                <button className="button small" type="button" onClick={() => link.mutate({ id: a.id, email: a.email })} disabled={a.status === 'disabled'}>
                  {t('user.resetPassword')}
                </button>
                <button className="button small" type="button" onClick={() => mfaReset.mutate(a.id)}>
                  {t('mfa.reset')}
                </button>
              </span>
            </div>
          ))}
          <form
            className="formgrid"
            style={{ marginBlockStart: 16 }}
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              addAdmin.mutate();
            }}
          >
            <Field label={t('platform.org.adminEmail')}>{(p) => <input {...p} type="email" dir="ltr" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} />}</Field>
            <Field label={t('platform.org.adminName')}>{(p) => <input {...p} value={adminName} onChange={(e) => setAdminName(e.target.value)} />}</Field>
            <div className="full">
              <button className="button" type="submit" disabled={addAdmin.isPending}>
                {t('platform.org.addAdmin')}
              </button>
            </div>
          </form>
        </div>
      </section>
      <PlatformIdentityPanel tenantId={tenantId} />
      <PlatformTenantEmailPanel tenantId={tenantId} />
      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>{t('platform.org.policy')}</h2>
            <p>{policy.data?.source === 'organisation' ? t('policy.source.organisation') : t('platform.org.policyInherit')}</p>
          </div>
          {policy.data?.source === 'organisation' ? (
            <button className="button small" type="button" onClick={() => void api('DELETE', `${P}/tenants/${tenantId}/policy`).then(() => qc.invalidateQueries({ queryKey: [...key, 'policy'] }))}>
              {t('platform.org.policyClear')}
            </button>
          ) : null}
        </div>
        <div className="pad">
          {policy.data ? (
            <PolicyForm
              key={`${policy.data.source}:${policy.data.version}`}
              policy={policy.data}
              submitLabel={policy.data.source === 'organisation' ? t('common.save') : t('platform.org.policyOverride')}
              save={(fields) => api<SecurityPolicyResponse>('PUT', `${P}/tenants/${tenantId}/policy`, { ...fields, ...(policy.data.source === 'organisation' ? { expectedVersion: policy.data.version } : {}) })}
              onSaved={() => void qc.invalidateQueries({ queryKey: [...key, 'policy'] })}
            />
          ) : (
            <Loading />
          )}
        </div>
      </section>
      {issued ? <ActivationLinkDialog name={issued.name} activation={{ activationToken: issued.token, expiresAt: issued.expiresAt }} url={orgUrl(o.hostnames[0] ?? '', `/activate#token=${issued.token}`)} onClose={() => setIssued(null)} /> : null}
    </>
  );
}

type PolicyFields = { [K in keyof typeof POLICY_BOUNDS]: number } & { mfaRequirement: MfaRequirement };

function PolicyForm({ policy, save, onSaved, submitLabel }: { policy: SecurityPolicyResponse; save: (f: PolicyFields) => Promise<SecurityPolicyResponse>; onSaved: () => void; submitLabel: string }) {
  const { t } = useI18n();
  const keys = Object.keys(POLICY_BOUNDS) as Array<keyof typeof POLICY_BOUNDS>;
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(keys.map((k) => [k, String(policy[k])])));
  const [mfa, setMfa] = useState<MfaRequirement>(policy.mfaRequirement);
  const m = useMutation({ mutationFn: () => save({ ...(Object.fromEntries(keys.map((k) => [k, Number(values[k])])) as Omit<PolicyFields, 'mfaRequirement'>), mfaRequirement: mfa }), onSuccess: onSaved });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <ErrorSummary error={m.error} fieldIds={Object.fromEntries(keys.map((k) => [k, `pol-${k}`]))} />
      <div className="formgrid">
        {keys.map((k) => (
          <Field key={k} id={`pol-${k}`} label={t(POLICY_LABELS[k])} hint={t('v.range', { min: POLICY_BOUNDS[k][0], max: POLICY_BOUNDS[k][1] })} error={fe[k]}>
            {(p) => <input {...p} type="number" min={POLICY_BOUNDS[k][0]} max={POLICY_BOUNDS[k][1]} value={values[k]} onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))} />}
          </Field>
        ))}
        <Field label={t('policy.mfaRequirement')} error={fe.mfaRequirement} full>
          {(p) => (
            <select {...p} value={mfa} onChange={(e) => setMfa(e.target.value as MfaRequirement)}>
              {MFA_REQUIREMENTS.map((r) => (
                <option key={r} value={r}>
                  {t(`policy.mfa.${r}`)}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>
      <p className="tiny" style={{ marginBlockStart: 12 }}>
        {t('policy.version', { version: policy.version })}
      </p>
      <div className="form-actions">
        <button className="button primary" type="submit" disabled={m.isPending}>
          {m.isPending ? t('common.saving') : submitLabel}
        </button>
      </div>
    </form>
  );
}

export function PlatformAccountPage() {
  const session = usePlatformSession();
  const qc = useQueryClient();
  if (!session.data) return <Loading />;
  return <AccountSecurity base={P} enrolled={session.data.mfa.enrolled} required={session.data.mfa.required} onChanged={() => void qc.invalidateQueries({ queryKey: PLATFORM_SESSION_KEY })} />;
}

export function PlatformPolicyPage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform', 'policy'], queryFn: () => get<SecurityPolicyResponse>(`${P}/policy`) });
  return (
    <>
      <PageHead eyebrow={t('platform.eyebrow')} title={t('platform.policy.title')} intro={t('platform.policy.intro')} />
      <section className="panel">
        <div className="pad">
          {q.isPending ? (
            <Loading />
          ) : q.isError ? (
            <ErrorState error={q.error} />
          ) : (
            <PolicyForm
              key={q.data.version}
              policy={q.data}
              submitLabel={t('common.save')}
              save={(fields) => api<SecurityPolicyResponse>('PUT', `${P}/policy`, { ...fields, expectedVersion: q.data.version })}
              onSaved={() => void qc.invalidateQueries({ queryKey: ['platform', 'policy'] })}
            />
          )}
        </div>
      </section>
    </>
  );
}
